import { ActionButton, Spinner, Stack, Text } from "@fluentui/react";
import Axios from "axios";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { AresDeploymentEnvironment } from "../API";
import { GetApiError } from "./ShowApiError";

type AresDeploymentGroupRolloutSnapshot = {
  id: number;
  versionNo: number;
  name: string;
  sourceHash?: string;
};

type AresDeploymentGroupRolloutCompany = {
  companyId: number;
  companyName: string;
  deploymentEnvironment: AresDeploymentEnvironment;
  isProtected: boolean;
  isMainDeploymentDatabase: boolean;
  isMainTestingDatabase: boolean;
};

type AresDeploymentGroupRolloutSummary = {
  groupId: number;
  groupName: string;
  productionSnapshot?: AresDeploymentGroupRolloutSnapshot;
  stagingSnapshot?: AresDeploymentGroupRolloutSnapshot;
  productionCompanies: AresDeploymentGroupRolloutCompany[];
  stagingCompanies: AresDeploymentGroupRolloutCompany[];
  testingCompanies: AresDeploymentGroupRolloutCompany[];
  resetCandidates: AresDeploymentGroupRolloutCompany[];
  protectedCompanies: AresDeploymentGroupRolloutCompany[];
};

type AresDeploymentRolloutSectionProps = {
  groupId?: number;
  onReleased?: () => Promise<unknown> | unknown;
};

const formatSnapshotLabel = (snapshot?: AresDeploymentGroupRolloutSnapshot) => {
  if (!snapshot) {
    return "Nije postavljen";
  }

  const snapshotName = snapshot.name || `v${snapshot.versionNo}`;
  return `${snapshotName} (v${snapshot.versionNo})`;
};

export const AresDeploymentRolloutSection: FC<
  AresDeploymentRolloutSectionProps
> = ({ groupId, onReleased }) => {
  const [summary, setSummary] = useState<AresDeploymentGroupRolloutSummary>();
  const [loading, setLoading] = useState(false);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [error, setError] = useState<string>();

  const loadSummary = useCallback(async (targetGroupId?: number) => {
    if (!targetGroupId) {
      setSummary(undefined);
      setError(undefined);
      return;
    }

    setLoading(true);
    setError(undefined);

    try {
      const { data } = await Axios.get<AresDeploymentGroupRolloutSummary>(
        `/AresDeploymentGroupRollout/Summary?groupId=${targetGroupId}`,
      );
      setSummary(data);
    } catch (e) {
      setSummary(undefined);
      setError(GetApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary(groupId);
  }, [groupId, loadSummary]);

  const stagingCompanies = summary?.stagingCompanies ?? [];
  const resetCandidates = summary?.resetCandidates ?? [];
  const protectedStagingCount = stagingCompanies.filter(
    (company) => company.isProtected,
  ).length;

  const hasBlockingProtectedStagingCompanies = useMemo(
    () => stagingCompanies.some((company) => company.isProtected),
    [stagingCompanies],
  );

  const confirmationMessage = useMemo(() => {
    if (!summary) {
      return "";
    }

    const stagingSnapshotName = formatSnapshotLabel(summary.stagingSnapshot);
    const resetCount = resetCandidates.length;

    return [
      `Promote staging snapshot ${stagingSnapshotName} to production?`,
      `Staging companies moved back to production: ${resetCount}.`,
      "Protected databases stay excluded: Glavna implementacijska baza, Glavna testna baza.",
    ].join("\n");
  }, [resetCandidates.length, summary]);

  const releaseStagingToProduction = useCallback(async () => {
    if (!groupId || !summary?.stagingSnapshot) {
      return;
    }

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    setReleaseLoading(true);
    setError(undefined);

    try {
      const { data } = await Axios.post<AresDeploymentGroupRolloutSummary>(
        `/AresDeploymentGroupRollout/Release?groupId=${groupId}`,
      );
      setSummary(data);
      await onReleased?.();
    } catch (e) {
      setError(GetApiError(e));
    } finally {
      setReleaseLoading(false);
    }
  }, [confirmationMessage, groupId, onReleased, summary?.stagingSnapshot]);

  const releaseDisabled =
    !groupId ||
    loading ||
    releaseLoading ||
    !summary?.stagingSnapshot ||
    hasBlockingProtectedStagingCompanies;

  return (
    <Stack
      tokens={{ childrenGap: 8 }}
      styles={{ root: { border: "1px solid #e1dfdd", borderRadius: 4, padding: 12 } }}
    >
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="mediumPlus">Release rollout</Text>
        <ActionButton disabled={releaseDisabled} onClick={releaseStagingToProduction}>
          {releaseLoading ? "Objavljujem…" : "Release staging to production"}
        </ActionButton>
      </Stack>

      {loading ? <Spinner label="Učitavam rollout…" /> : null}
      {error ? <Text styles={{ root: { color: "#a80000" } }}>{error}</Text> : null}

      {!loading && summary ? (
        <Stack tokens={{ childrenGap: 6 }}>
          <Text variant="small">
            Production snapshot: {formatSnapshotLabel(summary.productionSnapshot)}
          </Text>
          <Text variant="small">
            Staging snapshot: {formatSnapshotLabel(summary.stagingSnapshot)}
          </Text>
          <Text variant="small">
            Production: {summary.productionCompanies.length} | Staging:{" "}
            {summary.stagingCompanies.length} | Testing: {summary.testingCompanies.length} |
            Reset after release: {summary.resetCandidates.length}
          </Text>

          {hasBlockingProtectedStagingCompanies ? (
            <Text styles={{ root: { color: "#a4262c" } }}>
              Release je blokiran dok su {protectedStagingCount} zaštićene baze još na
              stagingu.
            </Text>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
};
